import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus, Search, UserRound, Building2, Mail, Phone } from 'lucide-react';

import { useApi } from '../../lib/api.js';
import { useCurrentUser } from '../../hooks/useCurrentUser.js';
import { PageHeader, LoadingPage, ErrorPage, EmptyState, StatusPill } from '../../components/ui/Feedback.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Select } from '../../components/ui/Form.jsx';
import { customerDisplayName, customerContactPerson } from '../../lib/customer.js';

// Free-text customer_type with a short, curated set surfaced here as quick
// filters. Users can type anything else on the form (datalist), but for list
// filtering we only expose the common buckets — anything more granular is
// search-driven.
const TYPE_OPTIONS = [
    { value: 'all', label: 'All types' },
    { value: 'Individual', label: 'Individual' },
    { value: 'Corporate', label: 'Corporate' },
    { value: 'Chauffeur partner', label: 'Chauffeur partner' },
    { value: 'Hotel', label: 'Hotel' },
    { value: 'Travel agency', label: 'Travel agency' },
    { value: 'Event organiser', label: 'Event organiser' },
];

export function CustomersListPage() {
    const api = useApi();
    const navigate = useNavigate();
    const { isAdmin } = useCurrentUser();
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('active');
    const [typeFilter, setTypeFilter] = useState('all');

    const { data, isLoading, isError, error, refetch } = useQuery({
        queryKey: ['customers', { status: statusFilter, customer_type: typeFilter, search }],
        queryFn: () => api.get('/customers', {
            query: {
                status: statusFilter || undefined,
                customer_type: typeFilter === 'all' ? undefined : typeFilter,
                search: search || undefined,
            }
        }),
    });

    const customers = data?.customers || [];

    const actions = isAdmin ? (
        <Button onClick={() => navigate('/customers/new')}>
            <Plus className="h-4 w-4" />
            New customer
        </Button>
    ) : null;

    return (
        <div>
            <PageHeader
                title="Customers"
                subtitle="Manage customer records, saved addresses, and account notes."
                actions={actions}
            />

            <div className="card mb-5">
                <div className="card-body">
                    <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-3">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-subtle pointer-events-none" />
                            <input
                                type="search"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search by name, email, or company…"
                                className="input pl-9"
                            />
                        </div>
                        <Select
                            value={typeFilter}
                            onChange={(e) => setTypeFilter(e.target.value)}
                            className="sm:w-44"
                            aria-label="Filter by customer type"
                        >
                            {TYPE_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </Select>
                        <Select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="sm:w-40"
                            aria-label="Filter by status"
                        >
                            <option value="">All statuses</option>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                        </Select>
                    </div>
                </div>
            </div>

            {isLoading && <LoadingPage />}
            {isError && <ErrorPage message={error?.message} onRetry={refetch} />}

            {!isLoading && !isError && customers.length === 0 && (
                <EmptyState
                    icon={UserRound}
                    title="No customers found"
                    description={search || typeFilter !== 'all' || statusFilter !== 'active'
                        ? 'Try adjusting your search or filters.'
                        : 'Add your first customer to get started.'}
                    action={isAdmin && (
                        <Button onClick={() => navigate('/customers/new')}>
                            <Plus className="h-4 w-4" />
                            Add customer
                        </Button>
                    )}
                />
            )}

            {!isLoading && !isError && customers.length > 0 && (
                <>
                    {/* Desktop table */}
                    <div className="hidden md:block card overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="bg-paper-hover border-b border-paper-border text-xs font-medium text-ink-subtle uppercase tracking-wider">
                                <tr>
                                    <th className="text-left px-6 py-3">Name</th>
                                    <th className="text-left px-6 py-3">Email</th>
                                    <th className="text-left px-6 py-3 w-40">Phone</th>
                                    <th className="text-left px-6 py-3 w-40">Type</th>
                                    <th className="text-left px-6 py-3 w-24">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {customers.map(c => (
                                    <tr
                                        key={c.id}
                                        onClick={() => navigate(`/customers/${c.id}`)}
                                        className="border-b border-paper-border-soft last:border-0 cursor-pointer hover:bg-paper-hover/50 transition-colors"
                                    >
                                        <td className="px-6 py-4">
                                            <div className="font-medium text-ink inline-flex items-center gap-1.5">
                                                {c.company_name && <Building2 className="h-3.5 w-3.5 text-ink-subtle flex-shrink-0" />}
                                                {customerDisplayName(c)}
                                            </div>
                                            {customerContactPerson(c) && (
                                                <div className="text-xs text-ink-subtle mt-0.5">
                                                    Contact: {customerContactPerson(c)}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-ink-muted">
                                            {c.email}
                                        </td>
                                        <td className="px-6 py-4 text-ink-muted">
                                            {c.phone || <span className="text-ink-faint">—</span>}
                                        </td>
                                        <td className="px-6 py-4">
                                            {c.customer_type ? (
                                                <span className="text-xs text-ink-muted">{c.customer_type}</span>
                                            ) : (
                                                <span className="text-ink-faint text-xs">—</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4">
                                            <StatusPill status={statusVariant(c.status)}>
                                                {c.status}
                                            </StatusPill>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile cards */}
                    <div className="md:hidden space-y-3">
                        {customers.map(c => (
                            <div
                                key={c.id}
                                onClick={() => navigate(`/customers/${c.id}`)}
                                className="card card-body cursor-pointer hover:shadow-card-hover transition-shadow"
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0 flex-1">
                                        <div className="font-medium text-ink inline-flex items-center gap-1.5">
                                            {c.company_name && <Building2 className="h-3 w-3 text-ink-subtle flex-shrink-0" />}
                                            {customerDisplayName(c)}
                                        </div>
                                        {customerContactPerson(c) && (
                                            <div className="text-xs text-ink-subtle mt-0.5">
                                                Contact: {customerContactPerson(c)}
                                            </div>
                                        )}
                                        <div className="flex items-center gap-3 mt-2 text-xs text-ink-muted">
                                            <span className="inline-flex items-center gap-1 min-w-0">
                                                <Mail className="h-3 w-3 flex-shrink-0" />
                                                <span className="truncate">{c.email}</span>
                                            </span>
                                            {c.phone && (
                                                <span className="inline-flex items-center gap-1">
                                                    <Phone className="h-3 w-3" />
                                                    {c.phone}
                                                </span>
                                            )}
                                        </div>
                                        {c.customer_type && (
                                            <div className="text-xs text-ink-subtle mt-1.5">{c.customer_type}</div>
                                        )}
                                    </div>
                                    <StatusPill status={statusVariant(c.status)}>{c.status}</StatusPill>
                                </div>
                            </div>
                        ))}
                    </div>

                    <p className="mt-4 text-xs text-ink-subtle">
                        {customers.length} {customers.length === 1 ? 'customer' : 'customers'}
                    </p>
                </>
            )}
        </div>
    );
}

function statusVariant(status) {
    switch (status) {
        case 'active': return 'success';
        case 'inactive': return 'neutral';
        default: return 'neutral';
    }
}
